import React from "react";
import { Alert, AlertTitle, Button } from "@mui/material";

import { Progress } from "ui";
import { StepStatus } from "ui/Progress/Progress";

import { useCustomWorkflow } from "fond/hooks/useCustomWorkflow";
import { useAppSelector } from "fond/utils/hooks";

const combinedWorkflowSteps = {
  preparing: [0],
  cleaning: [1],
  designInputs: [2],
};

interface DataPrepPanelProps {
  // Flag indicating if the editing is read only
  isReadOnly?: boolean;
}

const DataPrepPanel: React.FC<DataPrepPanelProps> = ({ isReadOnly }) => {
  const versionId = useAppSelector((state) => state.project.versionId);
  const { workflowState, workflowStepIndex, runWorkflow } = useCustomWorkflow({ versionId, key: "nbn_idd_lite_clean_data" });
  const isGenerating = workflowState === "STARTING" || workflowState === "IN_PROGRESS";

  const handleGenerate = async () => {
    runWorkflow?.();
  };

  /**
   * Determines the status of a step based on the workflow step index and state.
   */
  const getStatus = (steps: number[]): StepStatus => {
    if (workflowStepIndex == null) return "Idle";
    if (Math.max(...steps) < workflowStepIndex) return "Success";
    if (steps.includes(workflowStepIndex) && workflowState === "ERROR") return "Error";
    if (steps.includes(workflowStepIndex)) return "InProgress";
    if (workflowState !== "ERROR") return "Idle";

    return "NotReady";
  };

  return (
    <>
      {workflowState === undefined && (
        <Alert severity="info" variant="compact" sx={{ mb: 1 }}>
          Data is ready for cleaning.
        </Alert>
      )}

      {workflowState === "COMPLETE" && (
        <Alert severity="success" variant="compact" sx={{ mb: 1 }}>
          Data preparation complete.
        </Alert>
      )}

      {workflowState === "ERROR" && (
        <Alert severity="error" variant="compact" sx={{ mb: 2 }}>
          <AlertTitle>Data prepatation failed</AlertTitle>
          The data could not be prepared for the next step.
        </Alert>
      )}

      {(isGenerating || workflowState === "ERROR") && (
        <Progress
          steps={[
            {
              ID: "preparing",
              Label: "Preparing base data",
              Status: getStatus(combinedWorkflowSteps.preparing),
            },
            {
              ID: "cleaning",
              Label: "Cleaning geometries",
              Status: getStatus(combinedWorkflowSteps.cleaning),
            },
            {
              ID: "designInputs",
              Label: "Preparing design inputs",
              Status: getStatus(combinedWorkflowSteps.designInputs),
            },
          ]}
        />
      )}

      {workflowState !== "COMPLETE" && (
        <Button
          fullWidth
          variant="contained"
          size="small"
          sx={{ mt: 2 }}
          disabled={!runWorkflow || isGenerating || isReadOnly}
          onClick={handleGenerate}
        >
          Start
        </Button>
      )}
    </>
  );
};

export default DataPrepPanel;
